import * as signalR from '@microsoft/signalr';
import store from '../store';
import { FullBaseUrl } from '../constants';
import {
    truckHubConnection,
    truckHubWeight,
    truckHubReceipt,
    truckHubLocation,
} from '../actions/truckDataActions';
import { loadDriverRuns } from '../actions/runActions';
import { loadRunReceipts } from '../actions/receiptActions';

// Single shared connection to the cloud truck-proxy hub. The driver's browser receives live
// weight / receipt / connection updates here instead of polling the on-truck device over wifi.
let connection = null;
let subscribedNumberPlate = null;
let starting = null;

function buildConnection() {
    return new signalR.HubConnectionBuilder()
        .withUrl(`${FullBaseUrl()}/hubs/truckproxy`, {
            accessTokenFactory: () => {
                const user = store.getState().oidc.user;
                return user ? user.access_token : '';
            },
        })
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Warning)
        .build();
}

function registerHandlers(conn) {
    conn.on('WeightChanged', (weight) => {
        store.dispatch(truckHubWeight(weight));
    });

    conn.on('TruckConnectionChanged', (status) => {
        store.dispatch(truckHubConnection(status));
    });

    conn.on('LocationChanged', (location) => {
        store.dispatch(truckHubLocation(location));
    });

    conn.on('PickupCompleted', (receipt) => {
        store.dispatch(truckHubReceipt(receipt));
        // The pickup was completed server-side from the receipt; refresh the driver's runs so the
        // UI reflects the completion live.
        store.dispatch(loadDriverRuns(false));
        // Every submitted receipt broadcasts this event (assigned or not). Refresh the receipts for
        // the run the driver is currently viewing so the picker + list show it in realtime — this is
        // the primary way drivers pick up receipts now that Start Pumping is gone.
        const currentRunId = store.getState().runReceipts && store.getState().runReceipts.runId;
        if (currentRunId) {
            store.dispatch(loadRunReceipts(currentRunId));
        }
    });

    conn.onreconnected(() => {
        if (subscribedNumberPlate) {
            conn.invoke('SubscribeToTruck', subscribedNumberPlate).catch(() => {});
        }
    });
}

export function startTruckHub() {
    const user = store.getState().oidc.user;
    if (!user || !user.access_token) {
        return Promise.resolve();
    }
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
        return Promise.resolve();
    }
    if (starting) {
        return starting;
    }

    connection = buildConnection();
    registerHandlers(connection);

    starting = connection.start()
        .then(() => {
            if (subscribedNumberPlate) {
                return connection.invoke('SubscribeToTruck', subscribedNumberPlate).catch(() => {});
            }
        })
        .catch((err) => {
            console.log('Failed to connect to truck hub', err);
            connection = null;
        })
        .finally(() => {
            starting = null;
        });

    return starting;
}

// Watch a specific truck (the driver's active run's number plate). Re-subscribes if it changes.
export function subscribeToTruck(numberPlate) {
    if (!numberPlate || numberPlate === subscribedNumberPlate) {
        return;
    }

    const previous = subscribedNumberPlate;
    subscribedNumberPlate = numberPlate;

    const apply = () => {
        if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
            return;
        }
        if (previous && previous !== numberPlate) {
            connection.invoke('UnsubscribeFromTruck', previous).catch(() => {});
        }
        connection.invoke('SubscribeToTruck', numberPlate).catch(() => {});
    };

    startTruckHub().then(apply);
}

// Stop watching a truck (e.g. the office closing the Truck detail map). Leaves the shared
// connection open for any other watchers/driver run.
export function unsubscribeFromTruck(numberPlate) {
    if (!numberPlate) {
        return;
    }
    if (subscribedNumberPlate === numberPlate) {
        subscribedNumberPlate = null;
    }
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
        connection.invoke('UnsubscribeFromTruck', numberPlate).catch(() => {});
    }
}

export function stopTruckHub() {
    subscribedNumberPlate = null;
    if (connection) {
        const conn = connection;
        connection = null;
        return conn.stop().catch(() => {});
    }
    return Promise.resolve();
}
